/**
 * workspaceCardItems — Builds the WorkspaceCardItem[] array consumed by
 * <WorkspaceCards />.  Separated from the component so both
 * ProjectDetailPanel and WorkTabPanel can share it.
 *
 * Each item bundles a key, translated label, and SVG icon.
 */

import React from 'react';
import type { WorkspaceCardItem } from './WorkspaceCards';

/* ------------------------------------------------------------------ */
/*  SVG icons (inline, 18×18, stroke-based, inherits currentColor)     */
/* ------------------------------------------------------------------ */

const iconProps = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const PerformanceIcon = () => (
  <svg {...iconProps}>
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const ResourcesIcon = () => (
  <svg {...iconProps}>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const TeamIcon = () => (
  <svg {...iconProps}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const GanttIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="4" width="14" height="4" rx="1" />
    <rect x="5" y="10" width="16" height="4" rx="1" />
    <rect x="7" y="16" width="10" height="4" rx="1" />
  </svg>
);

/* Future workspace icons — ready when you add the tabs */

const AccountingIcon = () => (
  <svg {...iconProps}>
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

/** Balanced scale — currency-neutral icon for Budget tab. */
const BudgetIcon = () => (
  <svg {...iconProps}>
    {/* Center pillar */}
    <line x1="12" y1="3" x2="12" y2="21" />
    {/* Base */}
    <line x1="8" y1="21" x2="16" y2="21" />
    {/* Beam */}
    <line x1="3" y1="7" x2="21" y2="7" />
    {/* Left pan */}
    <path d="M3 7l2 6h4l2-6" />
    <line x1="5" y1="13" x2="9" y2="13" />
    {/* Right pan */}
    <path d="M13 7l2 6h4l2-6" />
    <line x1="15" y1="13" x2="19" y2="13" />
    {/* Fulcrum triangle */}
    <polygon points="10,3 14,3 12,1" fill="currentColor" stroke="none" />
  </svg>
);

/** Hands holding a heart — contributions / giving icon. */
const ContributionsIcon = () => (
  <svg {...iconProps}>
    <path d="M12 21C12 21 4 15 4 9.5C4 7 6 5 8.5 5C10 5 11.5 6 12 7.5C12.5 6 14 5 15.5 5C18 5 20 7 20 9.5C20 15 12 21 12 21Z" />
  </svg>
);

const RisksIcon = () => (
  <svg {...iconProps}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Builder                                                            */
/* ------------------------------------------------------------------ */

export interface WorkspaceCardLabels {
  performance: string;
  resources: string;
  budget: string;
  team: string;
  contributions: string;
  risksIssues: string;
  gantt: string;
}

/**
 * Build the card items array.
 *
 * @param labels   Translated labels (caller resolves via useAppTranslation)
 * @param options  Optional overrides (e.g. disable Gantt when no project)
 */
export function buildWorkspaceCardItems(
  labels: WorkspaceCardLabels,
  options?: {
    ganttDisabled?: boolean;
    /** Optional badge to overlay on the Risks & Issues card (open flag count). */
    risksIssuesBadge?: import('./WorkspaceCards').WorkspaceCardBadge;
  },
): WorkspaceCardItem[] {
  // Cognitive flow: state → threats → people → inputs → money out → money in → time.
  // Performance first (scorecard), Risks & Issues second (triage), then execution
  // surfaces (Team, Resources, Budget, Contributions), Gantt last as power tool.
  return [
    {
      key: 'performance',
      label: labels.performance,
      icon: <PerformanceIcon />,
    },
    {
      key: 'risksIssues',
      label: labels.risksIssues,
      icon: <RisksIcon />,
      badge: options?.risksIssuesBadge,
    },
    {
      key: 'team',
      label: labels.team,
      icon: <TeamIcon />,
    },
    {
      key: 'resources',
      label: labels.resources,
      icon: <ResourcesIcon />,
    },
    {
      key: 'budget',
      label: labels.budget,
      icon: <BudgetIcon />,
    },
    {
      key: 'contributions',
      label: labels.contributions,
      icon: <ContributionsIcon />,
    },
    {
      key: 'gantt',
      label: labels.gantt,
      icon: <GanttIcon />,
      disabled: options?.ganttDisabled,
    },
  ];
}

/* Re-export icons for potential reuse */
export {
  PerformanceIcon,
  ResourcesIcon,
  TeamIcon,
  GanttIcon,
  BudgetIcon,
  ContributionsIcon,
  AccountingIcon,
  RisksIcon,
};
